import React, { useState, useEffect } from "react";
import { resetPassword } from "../services/authService";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { isValidPassword } from "../utils/validation";
import { MESSAGES } from "../utils/messages";

const ResetPassword: React.FC = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [expired, setExpired] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // const [searchParams] = useSearchParams(); // OLD: used without expired state
  // const [loading, setLoading] = useState(false); // OLD: no expired state

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      navigate("/login");
    }
  }, [token, navigate]);

  if (!token) {
    return null;
  }

  const passwordChecks = [
    { label: "At least 8 characters", valid: newPassword.length >= 8 },
    { label: "One uppercase letter", valid: /[A-Z]/.test(newPassword) },
    { label: "One lowercase letter", valid: /[a-z]/.test(newPassword) },
    { label: "One number", valid: /[0-9]/.test(newPassword) },
    { label: "One special character", valid: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(newPassword) },
  ];

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error(MESSAGES.error.passwordMismatch);
      return;
    }

    const passwordError = isValidPassword(newPassword, {});
    if (passwordError) {
      toast.error(passwordError);
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, newPassword, confirmPassword);
      // Scenario 1: Password reset successful
      toast.success(MESSAGES.success.passwordReset);
      navigate("/login");
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || MESSAGES.error.passwordResetFailed;

      // Scenario 3: Expired token (410 Gone) — show request new link option
      if (status === 410) {
        setExpired(true);
        toast.error(msg);
      } else {
        // Scenario 2: Invalid token or other error
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Scenario 3: Expired token UI — offer to request a new reset link
  if (expired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-6 rounded shadow w-96 text-center">
          <h2 className="text-xl font-semibold mb-4">Link Expired</h2>
          <p className="text-gray-600 mb-4">
            {MESSAGES.error.expiredResetLink}
          </p>
          <a
            href="/forgot-password"
            className="btn-primary inline-block text-white px-4 py-2 rounded"
          >
            Request New Link
          </a>
          <div className="mt-4">
            <a href="/login" className="text-sm text-primary-600 hover:text-primary-500">
              Back to Login
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <form onSubmit={handleReset} className="bg-white p-6 rounded shadow w-96">
        <h2 className="text-xl font-semibold mb-4">Reset Password</h2>

        <input
          type="password"
          placeholder="New password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="input-field w-full mb-3"
          required
        />

        <input
          type="password"
          placeholder="Confirm password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="input-field w-full mb-3"
          required
        />

        {newPassword && confirmPassword && newPassword !== confirmPassword && (
          <p className="text-sm text-red-600 mb-3">{MESSAGES.error.passwordMismatch}</p>
        )}

        <div className="bg-gray-50 rounded p-3 mb-4 space-y-1.5">
          {passwordChecks.map((check) => (
            <div key={check.label} className="flex items-center text-sm">
              <span className={`mr-2 ${check.valid ? "text-green-500" : "text-gray-400"}`}>
                {check.valid ? "✓" : "○"}
              </span>
              <span className={check.valid ? "text-green-600" : "text-gray-500"}>{check.label}</span>
            </div>
          ))}
        </div>

        <button className="btn-primary w-full" disabled={loading}>
          {loading ? "Resetting..." : "Reset Password"}
        </button>
      </form>
    </div>
  );
};

export default ResetPassword;
